import {
  Box,
  Card,
  Tooltip,
  Toast,
  MaterialSymbol,
  BodyText,
  TransactionTypesOptions,
  TableProps,
  formattedCurrency,
  withStyles,
  useUpdateQueryParams,
  Workflow,
  TableHandle,
} from 'vopay';
import { FC, useEffect, useState, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import WorkflowSearchForm from './WorkflowSearchForm';
import useFetchWorkflows from '@/hooks/useApi/useFetchWorkflows';
import usePostUpdateWorkflow from '@/hooks/useApi/usePostUpdateWorkflow';
import useFetchSubAccounts from '@/hooks/useApi/useFetchSubAccounts';
import useFetchTransactionTypes from '@/hooks/useApi/useFetchTransactionTypes';
import { usePermissions } from '@/providers/Permissions.provider';
import { useDialog } from '@/providers/Dialog.provider';
import DeactivateWithSubAccountsDialog from './UpdateWorkFlow/DeactivateWithSubAccountsDialog';
import DeactivateWithoutSubAccountsDialog from './UpdateWorkFlow/DeactivateWithoutSubAccountsDialog';
import ActivateWithSubAccountsDialog from './UpdateWorkFlow/ActivateWithSubAccountsDialog';
import ActivateWithoutSubAccountsDialog from './UpdateWorkFlow/ActivateWithoutSubAccountsDialog';
import useFetchUserRoles from '@/hooks/useApi/useFetchUserRoles';
import { Trans, useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import { UtilButton, Button, TableFiltersContainer, Table, Title } from 'UI';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useDataFetchIndicator } from '@/hooks';
import styles from './WorkflowContainer.module.scss';

const cn = withStyles(styles);

type QueryParams = {
  paymentRails: string;
  status: string;
};

type AppState = {
  isCreate: boolean;
  hasCreateMultiple: boolean;
  isUpdate: boolean;
  paymentRailName: string;
};

type WorkflowWithType = Workflow & { TransactionType: TransactionTypesOptions };

const WorkflowContainer: FC = () => {
  const history = useHistory();
  const { permissions } = usePermissions();
  const dialog = useDialog();
  const [filteredList, setFilteredList] = useState<WorkflowWithType[]>([]);
  const [workflowName, setWorkflowName] = useState('');
  const [showWorkflowActivateToast, setShowWorkflowActivateToast] = useState(false);
  const [showWorkflowDeactivateToast, setShowWorkflowDeactivateToast] = useState(false);
  const [subAccountCount, setSubAccountCount] = useState(0);
  const { workflows, fetchWorkflows } = useFetchWorkflows();
  const [transactionTypesResponse, fetchTransactionTypes] = useFetchTransactionTypes({});
  const [, , postUpdateWorkflowAsync] = usePostUpdateWorkflow({});
  const [, , , fetchSubAccountsAsync] = useFetchSubAccounts({});
  const { fetchUserRoles } = useFetchUserRoles();
  const location = useLocation<AppState | undefined>();
  const state = location.state ? location.state : { isCreate: false, paymentRailName: '', isUpdate: false };
  const { isCreate, paymentRailName, isUpdate } = state;
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { t } = useTranslation('translations', { keyPrefix: 'TransactionApproval.Configuration.Workflows' });
  const alertDialog = useAlertDialog();
  const [, setLoading] = useDataFetchIndicator();
  const tableHandle = useRef<TableHandle>(null);
  const {
    response: { Country, subAccountPermissions },
  } = useClientData();

  const queryParams: QueryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      paymentRails: params.get('paymentRails') || '',
      status: params.get('status') || '',
    };
  }, [location.search]);
  const paymentRails = useMemo(() => queryParams.paymentRails || '', [queryParams]);
  const status = useMemo(() => queryParams.status || '', [queryParams]);
  const filtersApplied = useMemo(() => {
    return Boolean(paymentRails || status);
  }, [paymentRails, status]);
  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  useEffect(() => {
    refreshWorkflows();

    fetchTransactionTypes({
      CountriesID: Country === 'CA' ? 1 : 2,
      IsTransactionApprovalSupported: 1,
    });
  }, []);

  useEffect(() => {
    fetchUserRoles({
      Role: 'approver',
    });
  }, []);

  useEffect(() => {
    if (!subAccountPermissions) return;
    fetchSubAccountsAsync({ ActiveOnly: true }).then((res) => {
      if (res?.Success) setSubAccountCount(res.Subaccounts?.length ?? 0);
    });
  }, [subAccountPermissions]);

  useEffect(() => {
    if (showWorkflowActivateToast) {
      const timer = setTimeout(() => setShowWorkflowActivateToast(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showWorkflowActivateToast]);
  useEffect(() => {
    if (showWorkflowDeactivateToast) {
      const timer = setTimeout(() => setShowWorkflowDeactivateToast(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showWorkflowDeactivateToast]);

  type SortState = { id: string; order: 'asc' | 'desc' | 'none' };
  const [sortState, setSortState] = useState<SortState | null>(null);

  const sortedFilteredList = useMemo(() => {
    if (!sortState) return filteredList;
    const sorted = [...filteredList];
    sorted.sort((a, b) => {
      let valA: string | number = '';
      let valB: string | number = '';
      switch (sortState.id) {
        case 'name':
          valA = a.TransactionType || '';
          valB = b.TransactionType || '';
          break;
        case 'lastModified':
          valA = a.LastModified || '';
          valB = b.LastModified || '';
          break;
        case 'tiers':
          valA = workflows.find((g) => g.TransactionType === a.TransactionType)?.Workflows.length || 0;
          valB = workflows.find((g) => g.TransactionType === b.TransactionType)?.Workflows.length || 0;
          break;

        default:
          return 0;
      }
      if (valA < valB) return sortState.order === 'asc' ? -1 : 1;
      if (valA > valB) return sortState.order === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredList, sortState]);

  const showActivateDeactivateError = (errorMessage?: string) => {
    alertDialog.error(
      t('Dialogue.NotUpdatedError.title', { defaultValue: 'Error! The workflow was not updated' }),
      errorMessage || tGlobal('errorOccurred'),
      { abortLabel: t('Dialogue.NotUpdatedError.back', { defaultValue: 'Back' }) },
    );
  };

  const executeActivate = async (data: WorkflowWithType, applyToAllSubaccounts: boolean) => {
    const wfs = workflows.find((g) => g.TransactionType === data.TransactionType)?.Workflows || [];
    setLoading(true, '', 'fullScreen');
    try {
      const res = await Promise.all(
        wfs.map((wf) =>
          postUpdateWorkflowAsync({
            WorkflowID: wf.WorkflowID,
            IsActive: true,
            NumberApprovalsRequired: String(wf.NumberOfApprovalsRequired),
            MinAmount: wf.MinAmount,
            MaxAmount: wf.MaxAmount ? wf.MaxAmount : null,
            ApplyToAllSubaccounts: applyToAllSubaccounts,
          }),
        ),
      );
      const failed = res.find((r) => !r?.Success);
      if (failed) {
        showActivateDeactivateError(failed.ErrorMessage);
      } else {
        if (applyToAllSubaccounts) {
          const subAccountsRes = await fetchSubAccountsAsync({ ActiveOnly: true, ShowToken: true });
          if (subAccountsRes?.Success && subAccountsRes.Subaccounts) {
            await Promise.allSettled(
              subAccountsRes.Subaccounts.map(async (subAccount) => {
                const bearer = subAccount.Token;
                if (!bearer) return;
                const subWorkflowsRes = await fetchWorkflows(bearer);
                const subWfs = subWorkflowsRes?.Workflows.find((g) => g.TransactionType === data.TransactionType)?.Workflows || [];
                await Promise.all(
                  subWfs.map((wf) =>
                    postUpdateWorkflowAsync({
                      WorkflowID: wf.WorkflowID,
                      IsActive: true,
                      NumberApprovalsRequired: String(wf.NumberOfApprovalsRequired),
                      MinAmount: wf.MinAmount,
                      MaxAmount: wf.MaxAmount ? wf.MaxAmount : null,
                    }, bearer),
                  ),
                );
              }),
            );
          }
        }
        setWorkflowName(data.TransactionType);
        setShowWorkflowActivateToast(true);
        refreshWorkflows();
      }
    } catch {
      showActivateDeactivateError();
    } finally {
      setLoading(false);
    }
  };

  const executeDeactivate = async (data: WorkflowWithType, applyToAllSubaccounts: boolean) => {
    const wfs = workflows.find((g) => g.TransactionType === data.TransactionType)?.Workflows || [];
    setLoading(true, '', 'fullScreen');
    try {
      const res = await Promise.all(
        wfs.map((wf) =>
          postUpdateWorkflowAsync({
            WorkflowID: wf.WorkflowID,
            IsActive: false,
            NumberApprovalsRequired: String(wf.NumberOfApprovalsRequired),
            MinAmount: wf.MinAmount,
            MaxAmount: wf.MaxAmount ? wf.MaxAmount : null,
            ApplyToAllSubaccounts: applyToAllSubaccounts,
          }),
        ),
      );
      const failed = res.find((r) => !r?.Success);
      if (failed) {
        showActivateDeactivateError(failed.ErrorMessage);
      } else {
        if (applyToAllSubaccounts) {
          const subAccountsRes = await fetchSubAccountsAsync({ ActiveOnly: true, ShowToken: true });
          if (subAccountsRes?.Success && subAccountsRes.Subaccounts) {
            await Promise.allSettled(
              subAccountsRes.Subaccounts.map(async (subAccount) => {
                const bearer = subAccount.Token;
                if (!bearer) return;
                const subWorkflowsRes = await fetchWorkflows(bearer);
                const subWfs = subWorkflowsRes?.Workflows.find((g) => g.TransactionType === data.TransactionType)?.Workflows || [];
                await Promise.all(
                  subWfs.map((wf) =>
                    postUpdateWorkflowAsync({
                      WorkflowID: wf.WorkflowID,
                      IsActive: false,
                      NumberApprovalsRequired: String(wf.NumberOfApprovalsRequired),
                      MinAmount: wf.MinAmount,
                      MaxAmount: wf.MaxAmount ? wf.MaxAmount : null,
                    }, bearer),
                  ),
                );
              }),
            );
          }
        }
        setWorkflowName(data.TransactionType);
        setShowWorkflowDeactivateToast(true);
        refreshWorkflows();
      }
    } catch {
      showActivateDeactivateError();
    } finally {
      setLoading(false);
    }
  };

  const tableProps: TableProps<WorkflowWithType> = {
    id: 'scheduled-transactions-list',
    version: '2',
    zebra: true,

    onSort: (params) => {
      setSortState(params.order === 'none' ? null : { id: String(params.id), order: params.order });
    },
    columns: [
      {
        id: 'lastModified',
        header: t('Table.lastModified', { defaultValue: 'Last Modified' }),
        align: 'left',
        field: 'LastModified',
        sortable: true,
      },
      {
        id: 'name',
        header: t('Table.name', { defaultValue: 'Workflow Name' }),
        align: 'left',
        sortable: true,
        template: ({ data }) => (
          <Box ai="center">
            <UtilButton
              onClick={() => {
                history.push(`/transaction-approval/configuration/workflows/details/${data.WorkflowID}`);
              }}
            >
              {data.TransactionType}
            </UtilButton>
            <Tooltip
              customElement={
                <MaterialSymbol
                  icon={data.IsActive ? 'check_circle' : 'remove_circle'}
                  fillAmount={1}
                  color={data.IsActive ? '#008107' : '#626262'}
                />
              }
              hasButton={false}
              type="hover"
              position="bottom"
              information={
                <BodyText>
                  {data.IsActive
                    ? t('Table.active', { defaultValue: 'Active' })
                    : t('Table.inactive', { defaultValue: 'Inactive' })}
                </BodyText>
              }
            />
          </Box>
        ),
      },
      {
        id: 'tiers',
        header: t('Table.tiers', { defaultValue: 'Tiers' }),
        align: 'left',
        sortable: true,
        template: ({ data }) => {
          const workflow = workflows.find((wf) => wf.TransactionType === data.TransactionType)?.Workflows;

          return (
            <Box flx="col" gapSize="8px">
              {workflow?.map((wf, index) => (
                <div key={wf.WorkflowID} className={cn('tierContainer')}>
                  <div className={cn('tier')}>
                    {t('Table.tier', { defaultValue: 'Tier' })} {index + 1} • {wf.NumberOfApprovalsRequired}{' '}
                    {t('Table.approver', { count: Number(wf.NumberOfApprovalsRequired) })}
                  </div>
                  <div className={cn('range')}>
                    {index >= 1 && '> '}$ {formattedCurrency(wf.MinAmount)} →{' '}
                    {wf.MaxAmount ? `$ ${formattedCurrency(wf.MaxAmount)}` : t('noLimit', { defaultValue: 'No Limit' })}
                  </div>
                </div>
              ))}
            </Box>
          );
        },
      },
      ...(subAccountPermissions
        ? [
            {
              id: 'appliesToSubAccounts',
              header: t('Table.appliesToSubAccounts', { defaultValue: 'Applies to Sub-accounts' }),
              align: 'left' as const,
              template: ({ data }: { data: WorkflowWithType }) => {
                const group = workflows.find((wf) => wf.TransactionType === data.TransactionType);
                return (
                  <BodyText>
                    {group?.ApplyToAllSubaccounts
                      ? tGlobal('yes', { defaultValue: 'Yes' })
                      : tGlobal('no', { defaultValue: 'No' })}
                  </BodyText>
                );
              },
            },
          ]
        : []),
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => {
          return (
            <>
              {permissions.CreateTransactionApprovalWorkflow && (
                <Box gap="sm" jc="fe" ai="center">
                  {!data.IsActive && (
                    <Button
                      id={`workflow-edit-activate-${data.WorkflowID}`}
                      size="small"
                      onClick={async () => {
                        const group = workflows.find((g) => g.TransactionType === data.TransactionType);
                        const applyToAllSubaccounts = group?.ApplyToAllSubaccounts ?? false;

                        if (subAccountPermissions && applyToAllSubaccounts) {
                          const confirmed = await dialog.open<boolean>(({ onConfirm, onCancel }) => (
                            <ActivateWithSubAccountsDialog
                              subAccountCount={subAccountCount}
                              onConfirm={() => onConfirm(true)}
                              onCancel={onCancel}
                            />
                          ));
                          if (!confirmed) return;
                          await executeActivate(data, applyToAllSubaccounts);
                          return;
                        } else if (subAccountPermissions && !applyToAllSubaccounts) {
                          const confirmed = await dialog.open<boolean>(({ onConfirm, onCancel }) => (
                            <ActivateWithoutSubAccountsDialog
                              onConfirm={() => onConfirm(true)}
                              onCancel={onCancel}
                            />
                          ));
                          if (!confirmed) return;
                          await executeActivate(data, applyToAllSubaccounts);
                          return;
                        }

                        const decision = await alertDialog.caution(
                          t('Dialogue.ActivateConfirmation.title', { defaultValue: 'Activate Workflow?' }),
                          <Box flx="col">
                            <div>
                              {t('Dialogue.ActivateConfirmation.subTitleFirst', {
                                defaultValue:
                                  'Activating a workflow will affect your transactions and overwrite any previous approval workflow settings of this payment rail. Any transactions created above the minimum transaction limit will be moved to pending approval.',
                              })}
                            </div>
                            <div>
                              {t('Dialogue.ActivateConfirmation.subTitleSecond', {
                                defaultValue: 'Please ensure that approvers are correctly set up in the roles section.',
                              })}
                            </div>
                          </Box>,
                          {
                            abortLabel: t('Dialogue.ActivateConfirmation.cancelButton', {
                              defaultValue: 'Yes, Activate Workflow',
                            }),
                            confirmLabel: t('Dialogue.ActivateConfirmation.confirmButton', {
                              defaultValue: 'No, Go to Roles',
                            }),
                          },
                        );
                        if (decision !== 'confirm') {
                          if (decision === 'abort') history.push('/transaction-approval/configuration/roles');
                          return;
                        }
                        await executeActivate(data, applyToAllSubaccounts);
                      }}
                    >
                      {t('Table.Rows.activateButton', { defaultValue: 'Activate' })}
                    </Button>
                  )}
                  {data.IsActive && (
                    <Button
                      id={`workflow-edit-deactivate-${data.WorkflowID}`}
                      size="small"
                      onClick={async () => {
                        const group = workflows.find((g) => g.TransactionType === data.TransactionType);
                        const applyToAllSubaccounts = group?.ApplyToAllSubaccounts ?? false;

                        if (subAccountPermissions && applyToAllSubaccounts) {
                          const confirmed = await dialog.open<boolean>(({ onConfirm, onCancel }) => (
                            <DeactivateWithSubAccountsDialog
                              subAccountCount={subAccountCount}
                              onConfirm={() => onConfirm(true)}
                              onCancel={onCancel}
                            />
                          ));
                          if (!confirmed) return;
                          await executeDeactivate(data, applyToAllSubaccounts);
                          return;
                        } else if (subAccountPermissions && !applyToAllSubaccounts) {
                          const confirmed = await dialog.open<boolean>(({ onConfirm, onCancel }) => (
                            <DeactivateWithoutSubAccountsDialog
                              onConfirm={() => onConfirm(true)}
                              onCancel={onCancel}
                            />
                          ));
                          if (!confirmed) return;
                          await executeDeactivate(data, applyToAllSubaccounts);
                          return;
                        }

                        const decision = await alertDialog.caution(
                          t('Dialogue.DeactivateConfirmation.title', { defaultValue: 'Deactivate Workflow?' }),
                          <Box flx="col">
                            <div>
                              {t('Dialogue.DeactivateConfirmation.subTitleFirst', {
                                defaultValue:
                                  'Deactivating a workflow will affect your transactions and will remove any previous approval workflow settings of this payment rail.',
                              })}
                            </div>
                            <div>
                              {t('Dialogue.DeactivateConfirmation.subTitleSecond', {
                                defaultValue:
                                  'Any transactions currently pending approval will automatically be approved.',
                              })}
                            </div>
                          </Box>,
                          {
                            abortLabel: t('Dialogue.DeactivateConfirmation.cancelButton', {
                              defaultValue: 'Yes, Deactivate Workflow',
                            }),
                            confirmLabel: t('Dialogue.DeactivateConfirmation.confirmButton', {
                              defaultValue: 'No, Go to Roles',
                            }),
                          },
                        );
                        if (decision !== 'confirm') {
                          if (decision === 'abort') history.push('/transaction-approval/configuration/roles');
                          return;
                        }
                        await executeDeactivate(data, applyToAllSubaccounts);
                      }}
                    >
                      {t('Table.Rows.deactivateButton', { defaultValue: 'Deactivate' })}
                    </Button>
                  )}
                </Box>
              )}
            </>
          );
        },
      },
    ],
    rows: sortedFilteredList.map((workflow) => ({
      id: workflow.WorkflowID || '',
      data: workflow,
    })),
  };

  const filterResults = () => {
    const normalizedWorkflows: WorkflowWithType[] = workflows.map((group) => ({
      ...group.Workflows[0],
      NumberOfApprovalsRequired: Number(group.Workflows[0].NumberOfApprovalsRequired),
      TransactionType: group.TransactionType as TransactionTypesOptions,
    }));

    setFilteredList(
      normalizedWorkflows.filter((data) => {
        const ObjectStatus = data.IsActive ? 'active' : 'inactive';
        return (
          (paymentRails === '' || paymentRails === 'all' || data.TransactionType === paymentRails) &&
          (status === '' || status === 'all' || ObjectStatus === status)
        );
      }),
    );
  };

  useEffect(() => {
    filterResults();
  }, [workflows, paymentRails, status]);

  async function refreshWorkflows() {
    try {
      await fetchWorkflows();
    } catch (err) {
      alertDialog.error(tGlobal('error'), tGlobal('errorOccurred'));
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <UtilButton
          iconLeft="chevron_left"
          size="large"
          onClick={() => {
            history.replace('/transaction-approval/configuration');
          }}
        >
          {history.length > 1 ? tGlobal('Buttons.back') : t('backButton')}
        </UtilButton>
      </div>
      <Box flx="col" gapSize="36px">
        <Box jc="sb" ai="center">
          <Title>{t('title', { defaultValue: 'Approval Workflow' })}</Title>

          {permissions.CreateTransactionApprovalWorkflow &&
            transactionTypesResponse?.Success &&
            workflows.length !== (transactionTypesResponse?.TransactionTypes || []).length && (
              <Button
                id="create-workflow"
                colorScheme="primary"
                onClick={() => {
                  history.push('/transaction-approval/configuration/workflows/add');
                }}
              >
                {t('createButton', { defaultValue: 'Create Workflow' })}
              </Button>
            )}
        </Box>
        <Card
          style={{
            padding: '16px',
          }}
        >
          <Box flx="col" gap="sm">
            <TableFiltersContainer filtersApplied={filtersApplied}>
              <TableFiltersContainer.Trigger />
              <TableFiltersContainer.Content>
                <WorkflowSearchForm
                  paymentRails={paymentRails}
                  status={status}
                  transactionTypesResponse={transactionTypesResponse}
                  search={(searchParams) => {
                    setSortState(null);
                    tableHandle.current?.resetSort();
                    updateQueryParams({
                      paymentRails: searchParams.paymentRails || '',
                      status: searchParams.status || '',
                    });
                  }}
                />
              </TableFiltersContainer.Content>
            </TableFiltersContainer>
          </Box>
          <Box mb="sm">
            <Table ref={tableHandle} {...tableProps} />
          </Box>
        </Card>
      </Box>

      <Toast
        label={
          <Trans
            i18nKey="TransactionApproval.Configuration.Workflows.successfullyUpdatedToast"
            ns="translations"
            components={[<span style={{ fontWeight: 700 }} />]}
            values={{ paymentRailName }}
          />
        }
        isVisible={isCreate && isUpdate}
      />
      <Toast
        label={
          <Trans
            i18nKey="TransactionApproval.Configuration.Workflows.successfullyActivatedToast"
            ns="translations"
            components={[<span style={{ fontWeight: 700 }} />]}
            values={{ workflowName }}
          />
        }
        isVisible={showWorkflowActivateToast}
      />
      <Toast
        label={
          <Trans
            i18nKey="TransactionApproval.Configuration.Workflows.successfullyDeactivatedToast"
            ns="translations"
            components={[<span style={{ fontWeight: 700 }} />]}
            values={{ workflowName }}
          />
        }
        variant="neutral"
        isVisible={showWorkflowDeactivateToast}
      />
    </div>
  );
};
export default WorkflowContainer;
